import React, { useEffect } from "react";
import _ from "lodash";

import {
  Tooltip,
  ToggleSwitch,
  Dropdown,
  Button,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { CaretDownFilled, InformationOutlined } from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";
import classes from "./AssignmentCommonSettings.scss";
import { getShowResponseSettingsOptions } from "../Utils";

const styles = {
  tooltipOverlayInnerStyle: {
    maxWidth: "340px",
  },
};

const renderShowStudentResponseSetting = ({
  setting,
  t,
  updateSelectedItems,
}) => {
  const { value, disabled, testId, showSetting } = setting;
  if (!showSetting) {
    return null;
  }
  const showResponseSettingsOptions = getShowResponseSettingsOptions({ t });
  const updateInputField = e => {
    updateSelectedItems({ showStudentResponse: _.get(e, "key") });
  };
  return (
    <Dropdown
      dsVersion="2.0"
      overlay={
        <DropdownMenu
          options={showResponseSettingsOptions}
          onClick={updateInputField}
          value={value}
          showSelection={true}
          hasMultilineOptions={true}
          selectionType="radio"
          testId={testId}
        />
      }
      disabled={disabled}
      testId={testId}
    >
      <Button
        dsVersion="2.0"
        type="plain"
        size="medium"
        rightIcon={<CaretDownFilled />}
        className={classes.showStudentResponseButton}
        testId={testId}
      >
        {_.get(
          _.find(showResponseSettingsOptions, {
            key: value,
          }),
          "label"
        )}
      </Button>
    </Dropdown>
  );
};

const AssignmentCommonSettings = props => {
  const {
    commonSettings,
    updateSelectedItems,
    sourceAssignmentCommonSettings,
    doesStaffHaveAccessToAllClasses,
  } = props;
  const { t } = useI18n();

  useEffect(() => {
    // In case of duplicate assignment set initial value of common settings from source assignment
    if (!_.isEmpty(sourceAssignmentCommonSettings)) {
      _.forEach(sourceAssignmentCommonSettings, (value, key) => {
        const isSettingVisible = !_.isEmpty(
          _.find(commonSettings, { valueKey: key })
        );
        if (isSettingVisible) {
          updateSelectedItems({
            [key]: value,
          });
        }
      });
    }
  }, []);

  const renderSetting = setting => {
    const { key, value, valueKey, label, tooltip, disabled, testId } = setting;
    const handleToggleChange = () => {
      updateSelectedItems({
        [valueKey]: !value,
      });
    };

    return (
      <div key={key} className={classes.settingContainer}>
        <div className={classes.labelContainer}>
          <div className="text-label">{label}</div>
          {!_.isEmpty(tooltip) ? (
            <Tooltip
              dsVersion="2.0"
              tooltip={tooltip}
              placement={"right"}
              overlayInnerStyle={styles.tooltipOverlayInnerStyle}
              showArrow={true}
            >
              <InformationOutlined size={"xxx-small"} variant={"subtle"} />
            </Tooltip>
          ) : null}
        </div>
        <div className={classes.toggleContainer}>
          {_.isEqual(key, "SHOW_STUDENT_RESPONSE") ? (
            renderShowStudentResponseSetting({
              setting,
              t,
              updateSelectedItems,
            })
          ) : (
            <Tooltip
              dsVersion="2.0"
              tooltip={t(
                "classRoom:no_access_to_change_common_assignment_settings"
              )}
              placement={"left"}
              overlayInnerStyle={styles.tooltipOverlayInnerStyle}
              showArrow={true}
              trigger={!doesStaffHaveAccessToAllClasses ? ["hover"] : [null]}
            >
              <ToggleSwitch
                dsVersion="2.0"
                onChange={handleToggleChange}
                checked={value}
                disabled={disabled || !doesStaffHaveAccessToAllClasses}
                testId={testId}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.commonSettingsContainer}>
      {_.map(commonSettings, ({ onlyForViewMode = false, ...params }) =>
        !onlyForViewMode ? renderSetting(params) : null
      )}
    </div>
  );
};

export default React.memo(AssignmentCommonSettings);
